import FileDrop from './ui/FileDrop'
import FieldLabel from './ui/FieldLabel'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import edit from '../assets/edit.png'
import PdfViewer from './PdfViewer'
import { resolveStorageFileUrl, uploadFileToStorage } from '../storageUpload'
import { convertHeicBlob, heicToJpegObjectUrl, isHeicFile } from '../heicImage'

// The mobile app uploads PDFs without setting `pdf: true`, so the flag alone
// would push them into an <img> and show a broken image. Decide by the file
// itself as well, which is how the mobile viewer already does it.
const PDF_PATTERN = /\.pdf(\?|#|$)/i
export function isPdfFile(file) {
    if (!file || typeof file !== 'object') return false
    if (file.pdf === true) return true
    if ([file.mimeType, file.contentType].some(type => String(type || '').toLowerCase() === 'application/pdf')) return true
    return [file.name, file.filePath, file.storagePath, file.path, file.url]
        .filter(Boolean)
        .some(value => PDF_PATTERN.test(String(value)))
}

export default function MyDropzone({
    label,
    required,
    left,
    disabled,
    storageLocation,
    errors = [],
    name,
    editing,
    image,
    updateImage,
    allowPdf,
    allowPdfOnly,
    allowVideo,
    customMessage,
    setUploading,
    confidential = false,
    ownerWorkerId = ''
}) {
    const activeOrg = useSelector(state => state.user.activeOrg)
    const [editingImage, setEditingImage] = useState(false)
    const [numPages, setNumPages] = useState(null)
    const [pageNumber, setPageNumber] = useState(1)
    const [resolvedUrl, setResolvedUrl] = useState(null)
    const [displayUrl, setDisplayUrl] = useState(null)
    const [displayFailed, setDisplayFailed] = useState(false)
    const heic = isHeicFile(image)
    const pdf = isPdfFile(image)

    // ✅ Convert and upload
    const onDrop = useCallback(acceptedFiles => {
        if (disabled) return
        const file = acceptedFiles[0]
        // A file of a type this field does not accept arrives as an empty list.
        if (!file) {
            toast.error('That file type cannot be uploaded here.')
            return
        }
        const extension = file.type.replace('image/', '').replace('application/', '')

        if (extension === 'heic' || extension === 'heif') {
            // Conversion can take seconds; the page must treat it as uploading from
            // the start so Save cannot run before the photo is stored.
            if (setUploading) setUploading(true)
            convertHeicBlob(file, 'image/jpeg', 0.7).then(outputBlob => {
                const fileNew = new File([outputBlob], 'heic.jpg', {
                    type: 'image/jpeg',
                    lastModified: new Date().getTime(),
                })
                // doUploadFile clears the flag itself, whatever the outcome.
                return doUploadFile(fileNew, 'jpg')
            }).catch(() => {
                toast.error('That photo could not be converted. Try a JPEG or PNG instead.')
                if (setUploading) setUploading(false)
            })
        } else {
            doUploadFile(file, extension)
        }
    }, [disabled, setUploading])

    const doUploadFile = async (file, extension) => {
        try {
            if (setUploading) setUploading(true)
            const uploaded = await uploadFileToStorage({
                file,
                path: `${storageLocation}.${extension}`,
                mimeType: file.type || '',
                confidential,
                fallbackName: file.name || `upload.${extension}`,
                orgId: activeOrg?.id || '',
                ownerWorkerId,
            })
            updateImage({
                ...uploaded,
                pdf: extension.includes('pdf'),
                video: file.type.startsWith('video/'),
            })

            setEditingImage(false)
        } catch (error) {
            toast.error(error?.message ? `Upload failed: ${error.message}` : 'Upload failed. Please try again.')
        } finally {
            if (setUploading) setUploading(false)
        }
    }

    // ✅ Resolve confidential URL on mount
    useEffect(() => {
        let isCurrent = true
        setResolvedUrl(null)

        async function fetchSecureUrl() {
            if (image?.url) {
                try {
                    const resp = await resolveStorageFileUrl(image, activeOrg?.id || '')
                    if (isCurrent) {
                        setResolvedUrl(resp)
                    }
                } catch (err) {
                    if (isCurrent) {
                        setResolvedUrl(null)
                    }
                    console.error('Failed to fetch signed URL', err)
                }
            } else if (isCurrent) {
                setResolvedUrl(null)
            }
        }
        fetchSecureUrl()

        return () => {
            isCurrent = false
        }
    }, [image, activeOrg?.id])

    // Browsers other than Safari cannot draw a HEIC, so convert it to JPEG for
    // the preview. Everything else renders straight from the resolved URL.
    useEffect(() => {
        setDisplayFailed(false)
        if (!resolvedUrl || !heic || pdf || image?.video) {
            setDisplayUrl(resolvedUrl)
            return undefined
        }
        let isCurrent = true
        let objectUrl = null
        setDisplayUrl(null)
        heicToJpegObjectUrl(resolvedUrl)
            .then(url => {
                objectUrl = url
                if (isCurrent) setDisplayUrl(url)
                else URL.revokeObjectURL(url)
            })
            .catch(err => {
                console.error('HEIC preview failed', err)
                if (isCurrent) setDisplayFailed(true)
            })
        return () => {
            isCurrent = false
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [resolvedUrl, heic, pdf, image?.video])

    const {
        getRootProps,
        getInputProps,
        isFocused,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        accept: allowVideo
            ? { 'video/*': [] }
            : allowPdfOnly
                ? { 'application/pdf': [] }
                : allowPdf
                    ? { 'image/*': [], 'application/pdf': [] }
                    : { 'image/*': [] },
        onDrop,
    })

    const style = useMemo(() => ({ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', maxWidth: '300px', margin: left ? '0' : 'auto' }), [left])

    useEffect(() => {
        if (editing && !image && !editingImage) {
            setEditingImage(true)
        }
    }, [editing, image, editingImage])

    useEffect(() => {
        if (Array.isArray(errors) && errors.includes(name)) {
            document.getElementById(name)?.classList.add('errorImage')
        } else {
            document.getElementById(name)?.classList.remove('errorImage')
        }
    }, [errors, name])

    return (
        <div id={name} className="container" style={{ marginTop: '16px' }}>
            <FieldLabel className="fieldLabel">
                {label || ''}
                {required && <span className="uiRequired" style={{ color: '#dc2626' }}>*</span>}
            </FieldLabel>
            <FileDrop active={isFocused || isDragAccept} error={isDragReject} disabled={disabled} {...getRootProps({ style })}>
                {editingImage ? (
                    <>
                        <input disabled={disabled} {...getInputProps()} />
                        <FileDrop.Prompt hint={customMessage} />
                    </>
                ) : (
                    <>
                        {!disabled && editing && (
                            <img
                                src={edit}
                                alt="Replace"
                                style={{
                                    width: '16px',
                                    height: '16px',
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    cursor: 'pointer',
                                }}
                                onClick={() => !disabled && setEditingImage(true)}
                            />
                        )}
                        {resolvedUrl &&
                            (pdf ? (
                                <PdfViewer
                                    showSmall
                                    file={resolvedUrl}
                                    numPages={numPages}
                                    setNumPages={setNumPages}
                                    pageNumber={pageNumber}
                                    setPageNumber={setPageNumber}
                                />
                            ) : image?.video ? (
                                <video
                                    src={resolvedUrl}
                                    style={{ width: '100%', height: '100%' }}
                                    controls
                                />
                            ) : displayFailed ? (
                                // Never leave a broken image: if conversion fails, the original is still reachable.
                                <a href={resolvedUrl} target="_blank" rel="noopener noreferrer" onClick={event => event.stopPropagation()}>
                                    This photo couldn't be previewed. Open the original
                                </a>
                            ) : displayUrl ? (
                                <img
                                    src={displayUrl}
                                    alt={label || 'Uploaded image'}
                                    onError={() => setDisplayFailed(true)}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        maxHeight: '350px',
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <p role="status" style={{ margin: 0 }}>Loading image...</p>
                            ))}
                    </>
                )}
            </FileDrop>
        </div>
    )
}
